import { Box, Typography } from '@mui/material';
import { CustomDialog } from 'components/common/CustomDialog';
import { IconOutlineCalendar1 } from 'components/icons/components/outline/IconOutlineCalendar1';
import {
  SocialMediaListeningFilterByDateRangeButton,
  SocialMediaListeningPostDetailView,
} from 'features/socialMediaListening';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';
import { SocialListeningBrandMentionChart } from './SocialListeningBrandMentionChart';
import { SocialListeningBrandMentionPosts } from './SocialListeningBrandMentionPosts';

export const BRAND_MENTION_MODAL_TYPES = ['chart', 'posts', 'post'] as const;

type ModalType = (typeof BRAND_MENTION_MODAL_TYPES)[number];

interface Props {
  onClose: () => void;
  brandId: string;
}

export const SocialListeningBrandMentionDialog = ({
  onClose,
  brandId,
}: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedModalType = searchParams.get('modalType');
  const selectedPostId = searchParams.get('postId');

  const [modalType, setModalType] = useState<ModalType>(
    selectedModalType ? (selectedModalType as ModalType) : 'chart',
  );
  const [postId, setPostId] = useState<string | null>(selectedPostId || null);
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days').toDate(),
    endDate: moment().toDate(),
  });

  useEffect(() => {
    if (selectedModalType) {
      setModalType(selectedModalType as ModalType);
    } else {
      setModalType('chart');
    }
    if (selectedPostId) {
      setPostId(selectedPostId);
    } else {
      setPostId(null);
    }
  }, [selectedModalType, selectedPostId]);

  const onCloseDialog = () => {
    onClose();
    navigate(PlotRoutes.socialListeningCommunity(brandId));
  };

  return (
    <CustomDialog
      open
      onClose={onCloseDialog}
      titleBreadcrumbs={['Brand mentions']}
      onBackClick={() => {
        if (modalType !== 'chart') {
          navigate(-1);
        } else {
          onCloseDialog();
        }
      }}
      copyLink={`${window.location.origin}${
        window.location.pathname
      }?modalType=${modalType}${postId ? `&postId=${postId}` : ''}`}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          p: theme.spacing(8, 6),
        }}
      >
        {modalType !== 'post' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="headline-xl"
              fontSize={32}
              letterSpacing="-1.18px"
              fontWeight={500}
            >
              Brand Mentions
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Box id="brand-mention-dialog-filters" />
              <SocialMediaListeningFilterByDateRangeButton
                onChange={({ dateRange }) => {
                  if (dateRange[0] && dateRange[1]) {
                    setDateRange({
                      startDate: dateRange[0],
                      endDate: dateRange[1],
                    });
                  }
                }}
                selectedDateRange={dateRange}
                componentsProps={{
                  sx: {
                    background: theme.colors?.primary.parchment,
                  },
                  popoverSx: {
                    background: theme.colors?.primary.parchment,
                  },
                }}
              />
            </Box>
          </Box>
        )}
        {modalType === 'chart' && (
          <SocialListeningBrandMentionChart
            brandId={brandId}
            selectedDateRange={dateRange}
            onClickChartPoint={() => {
              setModalType('posts');
              navigate(`${window.location.pathname}?modalType=posts`);
            }}
          />
        )}
        {modalType === 'posts' && (
          <SocialListeningBrandMentionPosts
            brandId={brandId}
            dateRange={dateRange}
            onClickPost={(postId) => {
              setPostId(postId);
              setModalType('post');
              navigate(
                `${window.location.pathname}?modalType=post${
                  postId ? `&postId=${postId}` : ''
                }`,
              );
            }}
          />
        )}

        {postId && (
          <Box
            p={8.5}
            m={4}
            sx={{
              backgroundColor: theme.colors?.primary.white,
              borderRadius: theme.spacing(4),
            }}
          >
            <SocialMediaListeningPostDetailView
              brandId={brandId}
              socialPostId={postId}
              componentProps={{
                modalView: true,
              }}
            />
          </Box>
        )}
      </Box>
    </CustomDialog>
  );
};
